import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  DollarSign,
  Percent,
  SlidersHorizontal,
  TrendingUp,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import type {
  ScreenerFilter,
  ScreenerFilterOptions,
} from "@/schemas/screener.schema";

import { ScreenerService } from "../services/screener-service";

type AssetTypeScope = "stocks" | "etf" | "crypto" | "all";
type FilterKind = "multi-select" | "range" | "boolean";

interface FilterDefinition {
  key: string;
  label: string;
  kind: FilterKind;
  group:
    | "Classification"
    | "Size & Liquidity"
    | "Valuation"
    | "Growth"
    | "Profitability"
    | "Quality & Balance Sheet"
    | "Income";
  icon: typeof Building2;
  minKey?: keyof ScreenerFilter;
  maxKey?: keyof ScreenerFilter;
  arrayKey?: keyof ScreenerFilter;
  booleanKey?: keyof ScreenerFilter;
  optionsKey?: keyof ScreenerFilterOptions;
  scopes: AssetTypeScope[];
}

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    key: "sectors",
    label: "Sector",
    kind: "multi-select",
    group: "Classification",
    icon: Building2,
    arrayKey: "sectors",
    optionsKey: "sectors",
    scopes: ["stocks", "all"],
  },
  {
    key: "industries",
    label: "Industry",
    kind: "multi-select",
    group: "Classification",
    icon: Building2,
    arrayKey: "industries",
    optionsKey: "industries",
    scopes: ["stocks", "all"],
  },
  {
    key: "countries",
    label: "Country",
    kind: "multi-select",
    group: "Classification",
    icon: Building2,
    arrayKey: "countries",
    optionsKey: "countries",
    scopes: ["stocks", "etf", "all"],
  },
  {
    key: "exchanges",
    label: "Exchange",
    kind: "multi-select",
    group: "Classification",
    icon: Building2,
    arrayKey: "exchanges",
    optionsKey: "exchanges",
    scopes: ["stocks", "etf", "all"],
  },
  {
    key: "categories",
    label: "Category",
    kind: "multi-select",
    group: "Classification",
    icon: Building2,
    arrayKey: "categories",
    optionsKey: "categories",
    scopes: ["etf", "all"],
  },
  {
    key: "fund_families",
    label: "Fund Family",
    kind: "multi-select",
    group: "Classification",
    icon: Building2,
    arrayKey: "fund_families",
    optionsKey: "fund_families",
    scopes: ["etf", "all"],
  },
  {
    key: "sync",
    label: "Sync Enabled",
    kind: "boolean",
    group: "Classification",
    icon: SlidersHorizontal,
    booleanKey: "sync",
    scopes: ["stocks", "etf", "crypto", "all"],
  },
  {
    key: "market_cap",
    label: "Market Cap (USD)",
    kind: "range",
    group: "Size & Liquidity",
    icon: DollarSign,
    minKey: "min_market_cap",
    maxKey: "max_market_cap",
    scopes: ["stocks", "etf", "all"],
  },
  {
    key: "enterprise_value",
    label: "Enterprise Value",
    kind: "range",
    group: "Size & Liquidity",
    icon: DollarSign,
    minKey: "min_enterprise_value",
    maxKey: "max_enterprise_value",
    scopes: ["stocks", "all"],
  },
  {
    key: "average_volume",
    label: "Average Volume",
    kind: "range",
    group: "Size & Liquidity",
    icon: DollarSign,
    minKey: "min_average_volume",
    maxKey: "max_average_volume",
    scopes: ["stocks", "etf", "all"],
  },
  {
    key: "net_assets",
    label: "Net Assets",
    kind: "range",
    group: "Size & Liquidity",
    icon: DollarSign,
    minKey: "min_net_assets",
    maxKey: "max_net_assets",
    scopes: ["etf", "all"],
  },
  {
    key: "trailing_pe",
    label: "P/E",
    kind: "range",
    group: "Valuation",
    icon: DollarSign,
    minKey: "min_pe",
    maxKey: "max_pe",
    scopes: ["stocks", "all"],
  },
  {
    key: "forward_pe",
    label: "Forward P/E",
    kind: "range",
    group: "Valuation",
    icon: DollarSign,
    minKey: "min_forward_pe",
    maxKey: "max_forward_pe",
    scopes: ["stocks", "all"],
  },
  {
    key: "peg_ratio",
    label: "PEG",
    kind: "range",
    group: "Valuation",
    icon: DollarSign,
    minKey: "min_peg_ratio",
    maxKey: "max_peg_ratio",
    scopes: ["stocks", "all"],
  },
  {
    key: "price_to_book",
    label: "P/B",
    kind: "range",
    group: "Valuation",
    icon: DollarSign,
    minKey: "min_price_to_book",
    maxKey: "max_price_to_book",
    scopes: ["stocks", "all"],
  },
  {
    key: "latest_price_to_sales",
    label: "P/S",
    kind: "range",
    group: "Valuation",
    icon: DollarSign,
    minKey: "min_ps",
    maxKey: "max_ps",
    scopes: ["stocks", "all"],
  },
  {
    key: "latest_price_to_earnings",
    label: "P/E Valuation",
    kind: "range",
    group: "Valuation",
    icon: DollarSign,
    minKey: "min_pe_valuation",
    maxKey: "max_pe_valuation",
    scopes: ["stocks", "all"],
  },
  {
    key: "latest_price_to_fcf",
    label: "P/FCF",
    kind: "range",
    group: "Valuation",
    icon: DollarSign,
    minKey: "min_pfcf",
    maxKey: "max_pfcf",
    scopes: ["stocks", "all"],
  },
  {
    key: "revenue_growth",
    label: "Revenue Growth",
    kind: "range",
    group: "Growth",
    icon: TrendingUp,
    minKey: "min_revenue_growth",
    maxKey: "max_revenue_growth",
    scopes: ["stocks", "all"],
  },
  {
    key: "earnings_growth",
    label: "Earnings Growth",
    kind: "range",
    group: "Growth",
    icon: TrendingUp,
    minKey: "min_earnings_growth",
    maxKey: "max_earnings_growth",
    scopes: ["stocks", "all"],
  },
  {
    key: "gross_margins",
    label: "Gross Margin",
    kind: "range",
    group: "Profitability",
    icon: Percent,
    minKey: "min_gross_margins",
    maxKey: "max_gross_margins",
    scopes: ["stocks", "all"],
  },
  {
    key: "operating_margins",
    label: "Operating Margin",
    kind: "range",
    group: "Profitability",
    icon: Percent,
    minKey: "min_operating_margins",
    maxKey: "max_operating_margins",
    scopes: ["stocks", "all"],
  },
  {
    key: "profit_margins",
    label: "Profit Margin",
    kind: "range",
    group: "Profitability",
    icon: Percent,
    minKey: "min_profit_margins",
    maxKey: "max_profit_margins",
    scopes: ["stocks", "all"],
  },
  {
    key: "roe",
    label: "ROE",
    kind: "range",
    group: "Profitability",
    icon: Percent,
    minKey: "min_roe",
    maxKey: "max_roe",
    scopes: ["stocks", "all"],
  },
  {
    key: "roa",
    label: "ROA",
    kind: "range",
    group: "Profitability",
    icon: Percent,
    minKey: "min_roa",
    maxKey: "max_roa",
    scopes: ["stocks", "all"],
  },
  {
    key: "current_ratio",
    label: "Current Ratio",
    kind: "range",
    group: "Quality & Balance Sheet",
    icon: Percent,
    minKey: "min_current_ratio",
    maxKey: "max_current_ratio",
    scopes: ["stocks", "all"],
  },
  {
    key: "quick_ratio",
    label: "Quick Ratio",
    kind: "range",
    group: "Quality & Balance Sheet",
    icon: Percent,
    minKey: "min_quick_ratio",
    maxKey: "max_quick_ratio",
    scopes: ["stocks", "all"],
  },
  {
    key: "payout_ratio",
    label: "Payout Ratio",
    kind: "range",
    group: "Quality & Balance Sheet",
    icon: Percent,
    minKey: "min_payout_ratio",
    maxKey: "max_payout_ratio",
    scopes: ["stocks", "all"],
  },
  {
    key: "beta",
    label: "Beta",
    kind: "range",
    group: "Quality & Balance Sheet",
    icon: Percent,
    minKey: "min_beta",
    maxKey: "max_beta",
    scopes: ["stocks", "all"],
  },
  {
    key: "short_ratio",
    label: "Short Ratio",
    kind: "range",
    group: "Quality & Balance Sheet",
    icon: Percent,
    minKey: "min_short_ratio",
    maxKey: "max_short_ratio",
    scopes: ["stocks", "all"],
  },
  {
    key: "has_dividend",
    label: "Has Dividend",
    kind: "boolean",
    group: "Income",
    icon: Percent,
    booleanKey: "has_dividend",
    scopes: ["stocks", "etf", "all"],
  },
  {
    key: "dividend_yield",
    label: "Dividend Yield",
    kind: "range",
    group: "Income",
    icon: Percent,
    minKey: "min_dividend_yield",
    maxKey: "max_dividend_yield",
    scopes: ["stocks", "etf", "all"],
  },
  {
    key: "yield",
    label: "Yield",
    kind: "range",
    group: "Income",
    icon: Percent,
    minKey: "min_yield",
    maxKey: "max_yield",
    scopes: ["etf", "all"],
  },
  {
    key: "expense_ratio",
    label: "Expense Ratio",
    kind: "range",
    group: "Quality & Balance Sheet",
    icon: Percent,
    minKey: "min_expense_ratio",
    maxKey: "max_expense_ratio",
    scopes: ["etf", "all"],
  },
  {
    key: "ytd_return",
    label: "YTD Return",
    kind: "range",
    group: "Growth",
    icon: TrendingUp,
    minKey: "min_ytd_return",
    maxKey: "max_ytd_return",
    scopes: ["etf", "all"],
  },
  {
    key: "three_year_avg_return",
    label: "3Y Return",
    kind: "range",
    group: "Growth",
    icon: TrendingUp,
    minKey: "min_three_year_avg_return",
    maxKey: "max_three_year_avg_return",
    scopes: ["etf", "all"],
  },
  {
    key: "five_year_avg_return",
    label: "5Y Return",
    kind: "range",
    group: "Growth",
    icon: TrendingUp,
    minKey: "min_five_year_avg_return",
    maxKey: "max_five_year_avg_return",
    scopes: ["etf", "all"],
  },
];

const PRESETS: Record<string, Partial<ScreenerFilter>> = {
  large_cap: { min_market_cap: 10_000_000_000 },
  dividend: { has_dividend: true, min_dividend_yield: 0.02 },
  profitable: { min_profit_margins: 0.1, min_roe: 0.1 },
  reasonable_valuation: { max_pe: 25, max_ps: 6, max_price_to_book: 6 },
  high_quality: {
    min_roe: 0.15,
    min_operating_margins: 0.12,
    min_current_ratio: 1.2,
  },
  etf_low_fee: { max_expense_ratio: 0.005 },
};

interface ScreenerFiltersProps {
  filter: ScreenerFilter;
  onFilterChange: (filter: ScreenerFilter) => void;
  onClearFilters: () => void;
  assetType?: AssetTypeScope;
}

function NumericRangeField({
  label,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
}: {
  label: string;
  minValue: number | undefined;
  maxValue: number | undefined;
  onMinChange: (next: number | undefined) => void;
  onMaxChange: (next: number | undefined) => void;
}) {
  return (
    <div className="space-y-2 rounded-xl border border-border/50 bg-card p-4">
      <Label className="text-sm font-semibold">{label}</Label>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Min</Label>
          <Input
            type="number"
            value={minValue ?? ""}
            onChange={(event) =>
              onMinChange(
                event.target.value === ""
                  ? undefined
                  : Number(event.target.value),
              )
            }
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Max</Label>
          <Input
            type="number"
            value={maxValue ?? ""}
            onChange={(event) =>
              onMaxChange(
                event.target.value === ""
                  ? undefined
                  : Number(event.target.value),
              )
            }
          />
        </div>
      </div>
    </div>
  );
}

function countActiveFilters(
  filter: ScreenerFilter,
  definitions: FilterDefinition[],
) {
  return definitions.filter((definition) => {
    if (definition.kind === "multi-select" && definition.arrayKey) {
      const current = filter[definition.arrayKey];
      return Array.isArray(current) && current.length > 0;
    }
    if (definition.kind === "range" && definition.minKey && definition.maxKey) {
      return (
        filter[definition.minKey] !== undefined ||
        filter[definition.maxKey] !== undefined
      );
    }
    if (definition.kind === "boolean" && definition.booleanKey) {
      return filter[definition.booleanKey] !== undefined;
    }
    return false;
  }).length;
}

export function ScreenerFilters({
  filter,
  onFilterChange,
  onClearFilters,
  assetType = "all",
}: ScreenerFiltersProps) {
  const [filterOptions, setFilterOptions] =
    useState<ScreenerFilterOptions | null>(null);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await ScreenerService.getFilterOptions();
        setFilterOptions(options);
      } catch (error) {
        console.error("Failed to load filter options:", error);
      }
    };
    void loadFilterOptions();
  }, []);

  const visibleDefinitions = useMemo(
    () =>
      FILTER_DEFINITIONS.filter(
        (definition) =>
          definition.scopes.includes(assetType) ||
          definition.scopes.includes("all"),
      ),
    [assetType],
  );

  const groupedDefinitions = useMemo(
    () =>
      visibleDefinitions.reduce<Record<string, FilterDefinition[]>>(
        (acc, definition) => {
          acc[definition.group] = [
            ...(acc[definition.group] ?? []),
            definition,
          ];
          return acc;
        },
        {},
      ),
    [visibleDefinitions],
  );

  const activeCount = countActiveFilters(filter, visibleDefinitions);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative h-9 font-medium"
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 text-[10px]">
              {activeCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[460px] max-w-full p-0 sm:max-w-none">
        <div className="flex h-full flex-col">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>
              Browse all available filters and adjust them without leaving the
              panel.
            </SheetDescription>
          </SheetHeader>

          <div className="border-b px-6 py-4">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
              >
                Reset all
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  onFilterChange({ ...filter, ...PRESETS.large_cap, offset: 0 })
                }
              >
                Large Cap
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  onFilterChange({ ...filter, ...PRESETS.dividend, offset: 0 })
                }
              >
                Dividend
              </Button>
              {assetType === "stocks" && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onFilterChange({
                        ...filter,
                        ...PRESETS.profitable,
                        offset: 0,
                      })
                    }
                  >
                    Profitable
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onFilterChange({
                        ...filter,
                        ...PRESETS.reasonable_valuation,
                        offset: 0,
                      })
                    }
                  >
                    Reasonable Valuation
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onFilterChange({
                        ...filter,
                        ...PRESETS.high_quality,
                        offset: 0,
                      })
                    }
                  >
                    High Quality
                  </Button>
                </>
              )}
              {assetType === "etf" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onFilterChange({
                      ...filter,
                      ...PRESETS.etf_low_fee,
                      offset: 0,
                    })
                  }
                >
                  ETF Low Fee
                </Button>
              )}
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="space-y-8 px-6 py-6">
              {Object.entries(groupedDefinitions).map(
                ([group, definitions]) => {
                  const GroupIcon = definitions[0].icon;
                  return (
                    <section key={group} className="space-y-4">
                      <div className="flex items-center gap-2">
                        <GroupIcon className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">{group}</h3>
                      </div>

                      <div className="space-y-4">
                        {definitions.map((definition) => {
                          if (
                            definition.kind === "multi-select" &&
                            definition.arrayKey &&
                            definition.optionsKey
                          ) {
                            const current =
                              (filter[definition.arrayKey] as
                                | string[]
                                | undefined) ?? [];
                            const options =
                              filterOptions?.[definition.optionsKey] ?? [];
                            return (
                              <div
                                key={definition.key}
                                className="space-y-2 rounded-xl border border-border/50 bg-card p-4"
                              >
                                <Label className="text-sm font-semibold">
                                  {definition.label}
                                </Label>
                                <ScrollArea className="h-36 rounded-md border">
                                  <div className="space-y-1 p-2">
                                    {options.map((option) => (
                                      <button
                                        key={option}
                                        type="button"
                                        className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-muted"
                                        onClick={() => {
                                          const next = current.includes(option)
                                            ? current.filter(
                                                (item) => item !== option,
                                              )
                                            : [...current, option];
                                          onFilterChange({
                                            ...filter,
                                            [definition.arrayKey as string]:
                                              next.length > 0
                                                ? next
                                                : undefined,
                                            offset: 0,
                                          });
                                        }}
                                      >
                                        <Checkbox
                                          checked={current.includes(option)}
                                        />
                                        <span className="text-sm">
                                          {option}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </ScrollArea>
                              </div>
                            );
                          }

                          if (
                            definition.kind === "range" &&
                            definition.minKey &&
                            definition.maxKey
                          ) {
                            return (
                              <NumericRangeField
                                key={definition.key}
                                label={definition.label}
                                minValue={
                                  filter[definition.minKey] as
                                    | number
                                    | undefined
                                }
                                maxValue={
                                  filter[definition.maxKey] as
                                    | number
                                    | undefined
                                }
                                onMinChange={(next) =>
                                  onFilterChange({
                                    ...filter,
                                    [definition.minKey as string]: next,
                                    offset: 0,
                                  })
                                }
                                onMaxChange={(next) =>
                                  onFilterChange({
                                    ...filter,
                                    [definition.maxKey as string]: next,
                                    offset: 0,
                                  })
                                }
                              />
                            );
                          }

                          if (
                            definition.kind === "boolean" &&
                            definition.booleanKey
                          ) {
                            const value = filter[definition.booleanKey] as
                              | boolean
                              | undefined;
                            return (
                              <div
                                key={definition.key}
                                className="space-y-3 rounded-xl border border-border/50 bg-card p-4"
                              >
                                <Label className="text-sm font-semibold">
                                  {definition.label}
                                </Label>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant={
                                      value === true ? "default" : "outline"
                                    }
                                    size="sm"
                                    onClick={() =>
                                      onFilterChange({
                                        ...filter,
                                        [definition.booleanKey as string]: true,
                                        offset: 0,
                                      })
                                    }
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={
                                      value === false ? "default" : "outline"
                                    }
                                    size="sm"
                                    onClick={() =>
                                      onFilterChange({
                                        ...filter,
                                        [definition.booleanKey as string]: false,
                                        offset: 0,
                                      })
                                    }
                                  >
                                    No
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      onFilterChange({
                                        ...filter,
                                        [definition.booleanKey as string]:
                                          undefined,
                                        offset: 0,
                                      })
                                    }
                                  >
                                    Clear
                                  </Button>
                                </div>
                              </div>
                            );
                          }

                          return null;
                        })}
                      </div>

                      <Separator />
                    </section>
                  );
                },
              )}
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
